/**
 * Authentication Service
 * Handles user authentication using Custom Python Backend
 */

import { apiClient } from '../lib/api';
import { setAccessToken, clearAccessToken } from '../utils/auth-token';
import { User } from '../types/user.types';

/** How the email last signed in, and whether it can use a password. Anyone can use Google. */
export interface EmailStatus {
  status: 'password' | 'google' | 'new';
  has_password?: boolean;
}

export interface LoginResponse {
  action: 'success' | 'otp_required' | 'verify_email';
  access_token?: string;
  user?: User; // Backend might accept user data or we fetch it separately
  email?: string; // For OTP step
}

export interface RegisterResponse {
  message: string;
  action?: string;
  access_token?: string;
}

export interface DevLoginUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  clinic_id: number;
  is_verified: boolean;
  is_active: boolean;
}

export class AuthService {
  /**
   * Authenticate user with email and password
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/api/auth/login', {
      email,
      password,
    }, { silent: true });

    const data = response.data;

    // If login successful immediately (no 2FA), store token
    if (data.action === 'success' && data.access_token) {
      setAccessToken(data.access_token);
    }

    return data;
  }

  /**
   * List available local-development users for impersonation.
   */
  async listDevUsers(): Promise<DevLoginUser[]> {
    const response = await apiClient.get<DevLoginUser[]>('/api/auth/dev/users');
    return response.data;
  }

  /**
   * Local-development only: issue a normal JWT for a chosen user.
   */
  async devLoginAs(userId: number): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/api/auth/dev/login-as', {
      user_id: userId,
    });

    const data = response.data;
    if (data.action === 'success' && data.access_token) {
      setAccessToken(data.access_token);
    }

    return data;
  }

  /**
   * Which screen follows the one email box: log in, Google, or create an account.
   */
  async emailStatus(email: string): Promise<EmailStatus> {
    const response = await apiClient.post<EmailStatus>('/api/auth/email-status', { email }, { silent: true });
    return response.data;
  }

  /**
   * Register a new user. The account is signed in straight away; the emailed
   * confirmation link can wait.
   */
  async register(email: string, password: string, firstName: string, lastName: string): Promise<RegisterResponse> {
    const response = await apiClient.post<RegisterResponse>('/api/auth/register', {
      email,
      password,
      first_name: firstName,
      last_name: lastName,
      accepted_terms: true,
    }, { silent: true });
    if (response.data.access_token) {
      setAccessToken(response.data.access_token);
    }
    return response.data;
  }

  /**
   * Verify OTP for 2FA or Login
   */
  async verifyOtp(email: string, code: string): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/api/auth/verify-otp', {
      email,
      otp_code: code
    });

    const data = response.data;
    if (data.action === 'success' && data.access_token) {
      setAccessToken(data.access_token);
    }
    return data;
  }

  /**
   * Initiate Google OAuth sign-in
   */
  async initiateGoogleSignIn(): Promise<void> {
    // Redirect to backend endpoint which redirects to Google
    window.location.href = `${apiClient.defaults.baseURL}/api/auth/login/google`;
  }

  /**
   * Sign out
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/api/auth/logout');
    } catch (e) {
      console.warn('Logout failed on server, clearing local session anyway');
    }
    clearAccessToken();
  }

  /**
   * Send Password Reset Email
   */
  async sendPasswordReset(email: string): Promise<void> {
    await apiClient.post('/api/auth/forgot-password', { email });
  }

  /**
   * Reset Password with Token
   */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    await apiClient.post('/api/auth/reset-password', { 
      token, 
      new_password: newPassword 
    });
  }

  /**
   * Resend Verification Email
   */
  async resendVerificationEmail(email: string): Promise<void> {
    await apiClient.post('/api/auth/resend-verification', { email });
  }

  /**
   * Verify Email with Token
   */
  async verifyEmail(token: string): Promise<void> {
    await apiClient.post('/api/auth/verify-email', { token });
  }

  /**
   * Change Password (Authenticated Base)
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    const response = await apiClient.post('/api/auth/change-password', {
      old_password: oldPassword,
      new_password: newPassword
    });
    return response.data;
  }
}

// Export singleton instance
export const authService = new AuthService();
